import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Palette, RefreshCw, Gem, Gauge } from "lucide-react";

const Ramp = ({ hex }) => (
  <span className="inline-flex h-3.5 w-20 rounded-sm overflow-hidden border border-slate-200 align-middle">
    {(hex || []).map((c, i) => (
      <span key={i} className="flex-1" style={{ background: c }} />
    ))}
  </span>
);

export default function MapPaletteManager({ projectId, onApplied }) {
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState({});
  const [commodities, setCommodities] = useState([]);
  const [commodityDefaults, setCommodityDefaults] = useState({});
  const [commodityPalette, setCommodityPalette] = useState("prospectivity");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [pc, pp] = await Promise.all([
          api.get("/palettes"),
          api.get(`/projects/${projectId}/palettes`),
        ]);
        if (cancel) return;
        setCatalog(pc.data.catalog || []);
        setSel(pp.data.palettes || {});
        setCommodities(pp.data.commodities || []);
        setCommodityDefaults(pp.data.commodity_defaults || {});
        setCommodityPalette(pp.data.commodity_palette || "prospectivity");
        setLoaded(true);
      } catch {
        /* silent - panel simply won't populate */
      }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  const flat = catalog.flatMap((g) => g.palettes);
  const hexFor = (k) => (flat.find((p) => p.key === k) || {}).hex || [];

  const multi = (commodities || []).length > 1;

  // Build the list of prospectivity layers to render:
  //  - multi-commodity -> one colour ramp per commodity (key `prospectivity::<commodity>`)
  //  - single/none     -> one global prospectivity ramp (key `prospectivity`)
  const prospLayers = multi
    ? commodities.map((c) => ({
        key: `prospectivity::${c}`,
        label: `${c} Prospectivity`,
        def: commodityDefaults[c] || commodityPalette || "prospectivity",
        desc: `Colour ramp for the ${c} prospectivity & priority-target maps.`,
      }))
    : [{
        key: "prospectivity",
        label: "Prospectivity & Targets",
        def: commodityPalette || "prospectivity",
        desc: "Colour ramp for the fused prospectivity and priority-target maps.",
      }];

  const valueFor = (key, def) => sel[key] || def;
  const setLayer = (key, val) => setSel((p) => ({ ...p, [key]: val }));

  const resetDefaults = () => {
    setSel({});
    toast.info("Reset every commodity to its mineral-default palette - click Apply to re-render");
  };

  const apply = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/palettes`, { palettes: sel });
      await api.post(`/projects/${projectId}/areas/refresh-targets`);
      toast.success("Palettes saved - maps are re-rendering in the background and will refresh here shortly.");
      onApplied && onApplied();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  const PaletteSelect = ({ layerKey, def }) => (
    <Select value={valueFor(layerKey, def)} onValueChange={(v) => setLayer(layerKey, v)}>
      <SelectTrigger data-testid={`palette-select-${layerKey}`} className="h-11 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <Ramp hex={hexFor(valueFor(layerKey, def))} />
          <span className="truncate">{(flat.find((p) => p.key === valueFor(layerKey, def)) || {}).label || valueFor(layerKey, def)}</span>
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {catalog.map((grp) => (
          <SelectGroup key={grp.group}>
            <SelectLabel>{grp.group}</SelectLabel>
            {grp.palettes.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                <span className="flex items-center gap-2">
                  <Ramp hex={p.hex} /> {p.label}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="palette-manager">
      <div className="flex items-center gap-2 mb-1">
        <Palette className="text-orange-500" size={18} />
        <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Map Palette Manager
        </h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {multi
          ? "Customise the prospectivity colour ramp for each commodity. The Data Confidence ramp is shared across all commodities."
          : "Customise the colour ramp for each analysis layer. Mineral-default palettes are applied automatically per commodity."}
      </p>

      {multi && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#E5C06B] bg-[#C20511]/15 border border-[#C20511]/40 rounded-md px-2.5 py-1.5 mb-4 w-fit"
          data-testid="palette-multi-badge">
          <Gem size={12} /> Multi-commodity project · {commodities.length} prospectivity palettes
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5" data-testid="palette-prospectivity-group">
        {prospLayers.map((ly) => (
          <div key={ly.key} className="space-y-1.5">
            <Label className="text-slate-700 text-sm font-medium flex items-center gap-1.5">
              {multi && <Gem size={13} className="text-[#C20511]" />}{ly.label}
            </Label>
            <PaletteSelect layerKey={ly.key} def={ly.def} />
            <p className="text-[11px] text-slate-400">{ly.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100" data-testid="palette-confidence-group">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label className="text-slate-700 text-sm font-medium flex items-center gap-1.5">
              <Gauge size={13} className="text-emerald-600" /> Data Confidence <span className="text-[10px] font-normal text-slate-400">(global)</span>
            </Label>
            <PaletteSelect layerKey="confidence" def="confidence" />
            <p className="text-[11px] text-slate-400">Colour ramp for the multi-sensor data-confidence map (applies to every commodity).</p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className="border-slate-300 text-slate-600"
          onClick={resetDefaults} data-testid="palette-reset-btn">
          Reset to mineral defaults
        </Button>
        <Button type="button" size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
          onClick={apply} disabled={busy} data-testid="palette-apply-btn">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Applying…" : "Apply & Refresh Maps"}
        </Button>
      </div>
    </div>
  );
}
